import React, { useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { ScanEye } from "lucide-react";
import { useCad } from "../context/CadContext";
import { buildPreview } from "../lib/geometry";
import { BASE_COLOR } from "../constants/defaults";

function Scene() {
  const { layers, settings, base, selectedId, xray } = useCad();
  const built = useMemo(() => buildPreview(layers, settings, base), [layers, settings, base]);
  const totalH = Math.max(built.topZ || 4, 4);

  return (
    <>
      <color attach="background" args={["#070B12"]} />
      <ambientLight intensity={0.75} />
      <directionalLight position={[60, -40, 120]} intensity={1.5} />
      <directionalLight position={[-80, 60, 40]} intensity={0.5} />
      <hemisphereLight args={["#334155", "#0B1120", 0.4]} />
      <gridHelper args={[400, 40, "#1E293B", "#0F1a2e"]} rotation={[Math.PI / 2, 0, 0]} />
      <axesHelper args={[14]} />

      {built.baseGeoms.map((g, i) => (
        <mesh key={`base-${i}`} geometry={g}>
          <meshStandardMaterial color={BASE_COLOR} metalness={0.1} roughness={0.7} transparent={xray} opacity={xray ? 0.25 : 1} depthWrite={!xray} />
        </mesh>
      ))}

      {built.plates.map((p) =>
        p.visible === false ? null : (
          <group key={p.id}>
            <mesh geometry={p.geometry}>
              <meshStandardMaterial color={p.color} metalness={0.15} roughness={0.55} transparent={xray} opacity={xray ? 0.28 : 1} depthWrite={!xray}
                emissive={p.id === selectedId ? p.color : "#000000"} emissiveIntensity={p.id === selectedId ? 0.4 : 0} />
            </mesh>
            {p.pegGeoms.map((g, i) => (
              <mesh key={i} geometry={g} renderOrder={xray ? 2 : 0}>
                <meshStandardMaterial color={p.color} metalness={0.35} roughness={0.3} emissive={p.color} emissiveIntensity={xray ? 0.5 : 0.2} />
              </mesh>
            ))}
          </group>
        )
      )}

      <OrbitControls makeDefault enableDamping dampingFactor={0.1} target={[0, 0, totalH / 2]} />
    </>
  );
}

export function Viewport3D() {
  const { layers, base, xray, setXray } = useCad();
  const empty = !layers.length && !base;
  return (
    <div className="relative h-full w-full bg-[#070B12]">
      <div className="absolute top-2.5 left-3 z-10 text-[11px] font-mono text-slate-500 uppercase tracking-widest pointer-events-none">3D · Live Preview</div>
      <div className="absolute top-2.5 right-3 z-10 flex items-center gap-3">
        <button data-testid="xray-toggle-btn" onClick={() => setXray((v) => !v)}
          className={`h-7 px-2.5 flex items-center gap-1.5 rounded-md border text-[10px] font-mono uppercase tracking-wide transition-colors duration-150 ${xray ? "bg-sky-500/20 text-sky-300 border-sky-500/50" : "bg-slate-900/70 text-slate-400 border-slate-800 hover:text-white"}`}>
          <ScanEye size={13} /> X-Ray
        </button>
      </div>
      <div className="absolute bottom-2.5 right-3 z-10 text-[10px] font-mono text-slate-500 pointer-events-none">Pegs &amp; holes are colour-coded to their layer</div>
      {empty ? (
        <div className="h-full grid place-items-center text-slate-600 text-sm">Add a shape or import a base to begin</div>
      ) : (
        <Canvas data-testid="viewport-3d" dpr={[1, 2]} camera={{ position: [70, -85, 65], fov: 45, near: 0.1, far: 5000, up: [0, 0, 1] }} gl={{ antialias: true }}>
          <Scene />
        </Canvas>
      )}
    </div>
  );
}
